import { NuvoButton } from '@nuvolo/nuux-core/components/NuvoButton';
import { NuvoUpload } from '@nuvolo/nuux-core/components/NuvoUpload';
import { NuvoGrid } from '@nuvolo/nuux-core/components/NuvoGrid';
import { CardHeader, NuvoCard } from '@nuvolo/nuux-core/components/NuvoCard';
import styled from 'styled-components';
import { color } from '@nuvolo/nuux-theme/utils';
import FAIcon from '../FAIcon';
import useAttachmentManager, { MAX_TOTAL_STORAGE_BYTES } from './hooks/useAttachmentManager';
import { useCallback } from 'react';
import useToast from '#/common/hooks/useToast';

const numberWithCommas = (x: number | string): string =>
  x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');

const getIconByType = (mimeType: string): string => {
  if (mimeType && mimeType.includes('image')) {
    return 'image';
  }
  if (mimeType === 'uploading') {
    return 'hourglass-half';
  }
  return 'file';
};

const Styles = styled.div`
  .dx-button-content {
    color: ${color('custom', 'contrastText')} !important;
  }

  .wrapper {
    width: 100%;
    padding: 10px 20px;
  }

  .caption {
    margin: 40px 16px 16px 16px !important;
    padding: 20px 10px 10px !important;
    font-size: 20px;
    font-weight: 500;
    line-height: 26px;
    height: 26px;
    color: ${color('primary', 'dark')};
  }

  .btns-wrapper {
    border: 1px solid ${color('primary', 'light3')} !important;
    width: 100% !important;
    padding: 20px !important;
    margin: 0 !important;
    border-radius: 20px;
    background-color: ${color('custom', 'dataGridBackground')};
  }

  .dx-button {
    border-radius: 16px;
    padding: 12px 24px;
    background-color: ${color('ui', 'light2')};
    border: none !important;
    font-size: 14px;
    line-height: 20px;
    font-weight: 500;
    color: ${color('primary', 'dark')};
    width: 220px;
    height: 44px;
  }

  .dx-fileuploader-input-label {
    display: flex;
    justify-content: flex-start;
    align-items: center;
  }

  .cards-wrapper {
    padding: 20px !important;
    display: flex;
    flex-wrap: wrap;
    width: 100%;

    h1 {
      font-size: 14px;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .card {
      /* TBD */
    }
  }
  .cards-wrapper > div {
    margin-top: 10px;
    position: relative;
  }
`;

export const AttachmentUpload = ({
  relatedTable,
  relatedItem,
  disableUpload,
  tenantId,
}: {
  relatedTable: string;
  relatedItem: string;
  disableUpload?: boolean;
  tenantId?: string | null;
}) => {
  const {
    file,
    files,
    onSelectedFileChanged,
    upload,
    open,
    remove,
    currentlyDeleting,
    currentlyUploading,
    currentAttachments,
    totalUsedBytes,
    isStorageFull,
  } = useAttachmentManager({
    relatedTable,
    relatedItem,
    attachmentType: 'general',
    tenantId,
  });

  const { alert, confirm } = useToast();

  const handleUpload = useCallback(async () => {
    const fileSize = file?.size ?? 0;
    const projectedBytes = totalUsedBytes + fileSize;
    const projectedPercent = Math.round((projectedBytes / MAX_TOTAL_STORAGE_BYTES) * 100);

    if (projectedPercent >= 99) {
      void alert(
        'error',
        'Storage limit reached. Uploading this file would exceed your organization storage quota. Please remove old files or contact your administrator.',
      );
      return;
    }

    if (projectedPercent >= 80) {
      const confirmed = await confirm(
        'warning',
        `Uploading this file will bring your storage to ${projectedPercent}% of your organization quota. Do you want to continue?`,
        ['Cancel', 'Upload anyway'],
      );
      if (!confirmed) return;
    }

    try {
      await upload();
    } catch (err: any) {
      void alert('error', err?.message || 'Failed to upload file. Please try again.');
    }
  }, [upload, alert, confirm, file, totalUsedBytes]);

  const handleFileChanged = useCallback(
    (e: any) => {
      try {
        onSelectedFileChanged(e);
      } catch (err: any) {
        void alert('error', err?.message || 'Failed to select file.');
      }
    },
    [onSelectedFileChanged, alert],
  );

  const handleDeleteClick = (attachmentId: string, fileName: string) => {
    return (e: any) => {
      e?.stopPropagation?.();
      e?.preventDefault?.();
      if (disableUpload) {
        return;
      }
      void remove({ id: attachmentId, originalFileName: fileName });
    };
  };

  return (
    <Styles>
      <span className="caption">File Attachments</span>
      <div className="wrapper">
        <NuvoGrid container className="btns-wrapper">
          <NuvoGrid item xl={12} key="upload_btn">
            <NuvoButton
              label="Upload file"
              disabled={!files.length || isStorageFull}
              onClick={handleUpload}
            />
          </NuvoGrid>
          <NuvoGrid item xl={12} key="uploader">
            <NuvoUpload
              selectButtonText="Select file"
              // accept="image/*,application/pdf,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              accept="*"
              uploadMode="useForm"
              multiple
              value={files}
              onValueChanged={handleFileChanged}
              disabled={disableUpload || isStorageFull}
            />
          </NuvoGrid>

          <div className="cards-wrapper">
            {[...currentlyUploading, ...(currentAttachments || [])].map((a: any) => (
              <NuvoGrid item xs={12} sm={6} md={4} key={a.id} className="card">
                <NuvoCard key={a.id} onClick={() => open(a.id)}>
                  <CardHeader
                    title={a.originalFileName}
                    icon={{
                      source: 'other' as const,
                      icon: () => <FAIcon icon={getIconByType(a.mimeType)} />,
                    }}
                  />
                  {currentlyDeleting.includes(a.id)
                    ? 'Deleting...'
                    : `${numberWithCommas(Math.round(a.size / 1024))}KB`}
                </NuvoCard>
                {a.mimeType !== 'uploading' && !currentlyDeleting.includes(a.id) && (
                  <div
                    style={{
                      position: 'absolute',
                      top: '15px',
                      right: '15px',
                      cursor: 'pointer',
                    }}
                    onClick={handleDeleteClick(a.id, a.originalFileName)}>
                    <FAIcon icon="trash" />
                  </div>
                )}
              </NuvoGrid>
            ))}
          </div>
        </NuvoGrid>
      </div>
    </Styles>
  );
};

export default AttachmentUpload;
